import React, { useEffect, useRef, useState } from 'react'
import { supabase } from '../lib/supabaseClient'
import { useAuth } from '../context/AuthContext'
import { markMessagesAsRead } from '../lib/chatService'

type Message = {
  id: string
  order_id: string
  sender_id: string
  body: string
  created_at: string
  sender_role?: string
  sender?: {
    full_name: string | null
    role?: string
  }
}

type Props = {
  orderId: string
  orderStatus: string
  refreshKey?: number
}

const OrderChat: React.FC<Props> = ({ orderId, orderStatus, refreshKey = 0 }) => {
  const { profile } = useAuth()
  const [messages, setMessages] = useState<Message[]>([])
  const [newMessage, setNewMessage] = useState('')
  const [loading, setLoading] = useState(true)
  const bottomRef = useRef<HTMLDivElement | null>(null)

  const isChatDisabled = orderStatus === 'LIVREE' || orderStatus === 'ANNULEE'

  /* ----------------------------- LOAD ----------------------------- */
  const loadMessages = async () => {
    setLoading(true)

    const { data, error } = await supabase
      .from('messages')
      .select(`
        id,
        order_id,
        sender_id,
        sender_role,
        body,
        created_at,
        sender:profiles (full_name, role)
      `)
      .eq('order_id', orderId)
      .order('created_at', { ascending: true })

    if (!error) setMessages(data ?? [])
    setLoading(false)
  }

  useEffect(() => {
    if (!profile) return
    markMessagesAsRead(orderId, profile.id)
  }, [orderId])


  /* ----------------------------- SCROLL ----------------------------- */
  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: 'smooth' })
  }, [messages])

  /* ----------------------------- REALTIME ----------------------------- */
  useEffect(() => {
    loadMessages()

    const channel = supabase
      .channel(`order-chat-${orderId}`)
      .on(
        'postgres_changes',
        {
          event: 'INSERT',
          schema: 'public',
          table: 'messages',
          filter: `order_id=eq.${orderId}`,
        },
        payload => {
          const msg = payload.new as Message
          setMessages(prev =>
            prev.some(m => m.id === msg.id) ? prev : [...prev, msg]
          )
        }
      )
      .subscribe()

    return () => {
      supabase.removeChannel(channel)
    }
  }, [orderId, refreshKey])
  
  /* ----------------------------- SEND ----------------------------- */
  const sendMessage = async () => {
    if (isChatDisabled) return
    if (!newMessage.trim() || !profile) return

    const optimistic: Message = {
      id: `tmp-${Date.now()}`,
      order_id: orderId,
      sender_id: profile.id,
      sender_role: profile.role,
      body: newMessage,
      created_at: new Date().toISOString(),
      sender: {
        full_name: profile.full_name?? profile.email?? null,
        role: profile.role,
      },
    }

    setMessages(prev => [...prev, optimistic])
    setNewMessage('')

    const { error } = await supabase.from('messages').insert({
      order_id: orderId,
      sender_id: profile.id,
      sender_role: profile.role,
      body: optimistic.body,
    })

    if (error) {
      console.error(error)
      alert("Erreur lors de l'envoi du message.")
    }
  }

  /* ----------------------------- UI ----------------------------- */
  return (
    <div
      style={{
        border: '1px solid rgba(148,163,184,0.3)',
        borderRadius: '.75rem',
        padding: '.75rem',
        background: '#020617',
      }}
    >
      <h3 style={{ fontSize: '.9rem', marginBottom: '.5rem' }}>
        💬 Discussion
      </h3>

      {isChatDisabled && (
        <div
          style={{
            background: 'rgba(250,204,21,0.1)',
            color: '#facc15',
            fontSize: '.8rem',
            padding: '.5rem',
            borderRadius: '.5rem',
            marginBottom: '.5rem',
          }}
        >
          Le chat est désormais en lecture seule au vu du statut de la commande.
        </div>
      )}

      <div
        style={{
          maxHeight: '300px',
          overflowY: 'auto',
          marginBottom: '.5rem',
        }}
      >
        {loading && <p>Chargement…</p>}

        {!loading &&
          messages.map(msg => {
            const isMine = msg.sender_id === profile?.id
            return (
              <div
                key={msg.id}
                style={{
                  display: 'flex',
                  justifyContent: isMine ? 'flex-end' : 'flex-start',
                  marginBottom: '.25rem',
                }}
              >
                <div
                  style={{
                    maxWidth: '75%',
                    padding: '.5rem .75rem',
                    borderRadius: '.75rem',
                    background: isMine ? '#89a794ff' : '#1e293b',
                    color: isMine ? '#020617' : '#e5e7eb',
                    fontSize: '.85rem',
                  }}
                >
                  <div style={{ fontSize: '.7rem', opacity: 0.8 }}>
                    <span style={{ color: '#dbd5c8ff', fontSize: '0.9rem' }}>{msg.sender?.full_name || 'User'} </span>-{' '}
                    {new Date(msg.created_at).toLocaleString('fr-CH')}
                  </div>
                  <div style={{ color: '#f1efebff', fontSize: '1.0rem', marginTop: '0.5rem' }}>
                    {msg.body}
                  </div>
                </div>
              </div>
            )
          })}

        <div ref={bottomRef} />
      </div>

      <div style={{ display: 'flex', gap: '.5rem' }}>
        <input
          className="input"
          data-testid="orderchat-input"
          placeholder={
            isChatDisabled
              ? 'Chat désactivé (Statut bloqué de la commande)'
              : 'Écrire un message…'
          }
          value={newMessage}
          onChange={e => setNewMessage(e.target.value)}
          onKeyDown={e => e.key === 'Enter' && sendMessage()}
          disabled={isChatDisabled}
        />
        <button
          className="btn btn-primary"
          data-testid="orderchat-send"
          onClick={sendMessage}
          disabled={isChatDisabled}
        >
          Envoyer
        </button>
      </div>
    </div>
  )
}

export default OrderChat
